/**
 * Login structure: authentication flow supporting Google, SSO (fragment and
 * code grant), and dev-login modes.
 *
 * Partners register SSO providers via `registerLoginProviders`; when only one
 * provider is configured and `skipLoginProviderScreen` is set, the flow skips
 * the provider-picker screen entirely via `MinimalLoginScreen`. The structure
 * handles three redirect subscreens (`tokenRedirect`, `fragmentRedirect`, and
 * `codeRedirect`). Branding and copy are configurable through `LoginConfig`
 * slots.
 */
import { WarningAltFilled } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { useLegalDocLinks, useSiloName } from 'component/admin';
import { HorizBox, Pad, PadBox, WindowTitle } from 'component/basics';
import { CTAButton } from 'component/button';
import { DevLoginModal } from 'component/modal/login/dev-login-modal';
import { devLoginPersonas } from 'component/modal/login/dev-login-personas';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { RichText } from 'component/richtext';
import { ConversationScreen } from 'component/screen';
import { Heading, Paragraph, UtilityText } from 'component/text';
import { updateProfileAsync } from 'data/profiles';
import { FirstLoginSetup } from 'feature/profile/profile-photo-and-name';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, View } from 'react-native';
import { trackEvent } from 'util/analytics';
import { authClient, useSession, usesFirebaseAuth } from 'util/auth-client';
import type { Datastore } from 'util/datastore';
import { useDatastore, usePersonaKey, useSiloKey, useTrackEvent } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { fireAndForget } from 'util/hooks';
import type { LoginProvider } from 'util/loginproviders';
import { silosWithoutGoogleLogin } from 'util/loginproviders';
import { takeSignInContext } from 'util/sign-in-context';
import type { LoginBranding, Structure, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import {
    assembleUrl,
    boolToString,
    getApiBaseUrl,
    getBaseUrl,
    getDeploymentConfig,
    makeAssetUrl,
    makeRandomNonce,
    toBool,
} from 'util/util';

const deploymentConfig = getDeploymentConfig();

export type LoginConfig = {
    loginHeadingLabel: TLabel;
    loginHeadingLabelAdmin: TLabel;
    loginSubheadingLabel?: TLabel;
    loginSubheadingLabelAdmin: TLabel;
    showCookieNotice: boolean;
    brandLogoImages?: string[];
    loginPreviewImage: string;
};

export const LoginStructure: Structure<LoginConfig> = {
    key: 'login',
    name: 'Login',
    screen: LoginScreen,
    subscreens: {
        tokenRedirect: TokenRedirectScreen,
        fragmentRedirect: FragmentRedirectScreen,
        codeRedirect: CodeRedirectScreen,
    },
    receivesConfigDefaults: {
        loginHeadingLabel: t('login.default_login_heading_label', 'A space for healthy discussions and varied perspectives'),
        loginSubheadingLabel: undefined,
        loginHeadingLabelAdmin: t('login.default_admin_login_heading_label', 'Welcome to {psiBrandName}'),
        loginSubheadingLabelAdmin: t('login.default_admin_login_subheading_label', 'Log in with your Admin account to get started'),
        showCookieNotice: true,
        loginPreviewImage: 'images/admin_login_image-v3.png',
    },
};

/**
 * Overlays a silo's branding onto the ordinary login config.
 *
 * Branding declares its labels as plain strings, because a silo that brands
 * its login screen serves a single language, so the strings are written in
 * that language rather than translated. They become untranslated `TLabel`s
 * here. Fields the branding leaves unset are omitted rather than spread as
 * `undefined`, which would override a default such as `showCookieNotice`.
 */
function applyLoginBranding(config: LoginConfig, branding: LoginBranding): LoginConfig {
    return {
        ...config,
        ...(branding.loginHeadingLabel !== undefined && { loginHeadingLabel: { key: null, defaultText: branding.loginHeadingLabel } }),
        ...(branding.loginSubheadingLabel !== undefined && {
            loginSubheadingLabel: { key: null, defaultText: branding.loginSubheadingLabel },
        }),
        ...(branding.showCookieNotice !== undefined && { showCookieNotice: branding.showCookieNotice }),
        ...(branding.brandLogoImages !== undefined && { brandLogoImages: branding.brandLogoImages }),
        ...(branding.loginPreviewImage !== undefined && { loginPreviewImage: branding.loginPreviewImage }),
    };
}

/**
 * Login config with the active silo's branding applied.
 *
 * Which silos are branded is deployment configuration rather than an instance
 * feature, so it comes from `SiloConfig.loginBranding` and is read
 * synchronously — the login screen renders pre-auth, and an async read would
 * paint the default branding first. Memoised so the labels it builds keep a
 * stable identity across renders, the way `t()` interns its own.
 */
function useLoginConfig(): LoginConfig {
    const config = useConfig<LoginConfig>();
    const siloKey = useSiloKey();
    const branding = getDeploymentConfig()?.siloConfig?.[siloKey]?.loginBranding;

    return useMemo(() => (branding ? applyLoginBranding(config, branding) : config), [config, branding]);
}

export type LoginScreenProps = { action: string };

export function LoginScreen({ action }: LoginScreenProps) {
    useTrackEvent('login-screen', { action });
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const siloKey = useSiloKey();

    useLoginListener();

    useEffect(() => {
        if (personaKey) {
            datastore.goBackAfterLogin({ action });
        }
    }, [personaKey, datastore, action]);

    const shouldSkipLoginScreen = checkIfLoginScreenCanBeSkipped({ siloKey });

    return shouldSkipLoginScreen ? <MinimalLoginScreen action={action} /> : <UnauthenticatedLoginScreen action={action} />;
}

/**
 * Automatically opens the login provider's or Google's login popup and
 * immediately redirects to the previous screen
 */
function MinimalLoginScreen({ action }: { action: string }) {
    const session = useSession();
    const datastore = useDatastore();
    const siloKey = useSiloKey();
    const isLoggedIn = !!usePersonaKey();
    const hasGoogleLogin: boolean = usesFirebaseAuth && silosWithoutGoogleLogin.indexOf(siloKey) === -1;
    const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);

    const applicableLoginProviders: LoginProvider[] = loginProviders.filter((loginProvider) => loginProvider.silos.includes(siloKey));
    const firstLoginProvider: LoginProvider = applicableLoginProviders[0];

    useEffect(() => {
        if (firstLoginProvider && !hasAttemptedLogin && !isLoggedIn) {
            setHasAttemptedLogin(true);
            fireAndForget(loginWithProviderAsync({ datastore, siloKey, loginProvider: firstLoginProvider }));
            datastore.goBack();
        } else if (hasGoogleLogin && !hasAttemptedLogin && !isLoggedIn) {
            setHasAttemptedLogin(true);
            fireAndForget(loginWithGoogleAsync({ datastore, session }));
            datastore.goBack();
        } else if (isLoggedIn) {
            datastore.goBackAfterLogin({ action });
        }
    }, [hasAttemptedLogin, isLoggedIn, firstLoginProvider, hasGoogleLogin, datastore, siloKey, action, session]);

    return <LoadingSpinnerContainer />;
}

async function loginWithGoogleAsync({ datastore, session }: { datastore: Datastore; session: { refetch: () => Promise<void> } }) {
    trackEvent(datastore, 'login-request', { method: 'google' });
    await authClient.signIn.google();
    await session.refetch();
}

async function loginWithProviderAsync({
    datastore,
    siloKey,
    loginProvider,
}: {
    datastore: Datastore;
    siloKey: string;
    loginProvider: LoginProvider;
}) {
    trackEvent(datastore, 'login-request', { method: loginProvider.key });

    const fragmentRedirectUrl = getBaseUrl() + siloKey + '/login/one/fragmentRedirect';
    const codeRedirectUrl = getBaseUrl() + siloKey + '/login/one/codeRedirect';
    const redirect_uri = loginProvider.mode === 'code' ? codeRedirectUrl : fragmentRedirectUrl;
    const nonce = makeRandomNonce();

    const stateChunk = makeStateChunk({
        siloKey,
        provider: loginProvider.key,
        debug: toBool(deploymentConfig.ssoDebug),
        noPopup: loginProvider.noPopup,
    });

    const extraParams = { ...loginProvider.extraParams };
    if (extraParams.ui_locales && typeof document !== 'undefined') {
        extraParams.ui_locales = document.documentElement.lang || extraParams.ui_locales;
    }

    const loginUrl = assembleUrl(loginProvider.authUrl, {
        state: stateChunk,
        nonce,
        client_id: loginProvider.clientId,
        redirect_uri,
        scope: loginProvider.scope!,
        ...extraParams,
    });

    if (loginProvider.noPopup) {
        datastore.gotoUrl(loginUrl);
    } else {
        datastore.openUrl(loginUrl, 'Login with ' + loginProvider.name, 'width=600,height=600');
    }
}

export function LoginBlockedScreen() {
    const s = useLoginBlockedScreenStyles();
    const { colors } = useTheme();
    const siloKey = useSiloKey();
    const siloName = useSiloName() ?? siloKey.toUpperCase();

    return (
        <ConversationScreen>
            <PadBox horiz={20} vert={20}>
                <View style={s.warningBox}>
                    <WarningAltFilled color={colors.colorTextOnTagLight} size={20} />
                </View>
                <Pad size={24} />
                <Heading
                    label={t('login.account_blocked_heading', 'You cannot login because your account is blocked')}
                    type='large'
                    weight='medium'
                />
                <Pad size={16} />
                <Paragraph
                    label={t(
                        'login.account_blocked_explanation',
                        'No action is necessary for the beta. Only a Moderator can restore your discussion access.',
                    )}
                    type='small'
                />
                <Pad size={16} />
                <Paragraph
                    formatParams={{ siloName }}
                    label={t('login.account_not_impacted', 'Your {siloName} account is not impacted and still accessible.')}
                    strong
                    type='small'
                />
                <Pad size={32} />
                <CTAButton label={t('login.okay_button', 'Okay')} onPress={() => authClient.signOut()} type='primary' />
            </PadBox>
        </ConversationScreen>
    );
}

const useLoginBlockedScreenStyles = createThemedStyles((tokens) => ({
    warningBox: {
        backgroundColor: tokens.colors.colorTextWarning,
        height: 48,
        width: 48,
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

export function OnboardingScreen({ onComplete }: { onComplete?: () => void }) {
    const datastore = useDatastore();

    async function onFieldsChosen({ updates }: { updates: any }) {
        await updateProfileAsync(datastore, { ...updates, consentAccepted: true });

        // Updating the profile may grant the user the owner role if this is a
        // new silo. FIXME: make silo creation an explicit operation
        await datastore.refreshRolesAsync();

        trackEvent(datastore, 'profile-setup', {}, takeSignInContext());
        onComplete?.();
    }

    return (
        <ConversationScreen>
            <PadBox horiz={20} vert={20}>
                <FirstLoginSetup onFieldsChosen={onFieldsChosen} />
            </PadBox>
        </ConversationScreen>
    );
}

/**
 * Returns `true` if there is only 1 login option including Google and if the
 * `skipLoginProviderScreen` flag is set in the deployment config
 */
function checkIfLoginScreenCanBeSkipped({ siloKey }: { siloKey: string }): boolean {
    const deploymentConfig = getDeploymentConfig();

    const applicableLoginProviders: LoginProvider[] = loginProviders.filter((loginProvider) => loginProvider.silos.includes(siloKey));

    // The Google login is not explicitly listed in loginProviders
    const showGoogle: boolean = usesFirebaseAuth && silosWithoutGoogleLogin.indexOf(siloKey) === -1;
    const loginOptionCount: number = applicableLoginProviders.length + (showGoogle ? 1 : 0);

    return !!deploymentConfig.skipLoginProviderScreen && loginOptionCount === 1;
}

function LoginHeader({ headingLabel, subheadingLabel }: { headingLabel: TLabel; subheadingLabel: TLabel | undefined }) {
    const s = useUnauthenticatedLoginScreenStyles();
    const { colors } = useTheme();
    const { loginPreviewImage, brandLogoImages } = useLoginConfig();
    const deploymentConfig = getDeploymentConfig();
    const psiBrandName = deploymentConfig.psiBrandName ?? 'PSI';

    return (
        <PadBox left={40} right={40}>
            {brandLogoImages && (
                <>
                    <View style={s.imageWrapper}>
                        <HorizBox gap={32}>
                            {brandLogoImages.map((brandLogoImage, index) => (
                                <Image
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={brandLogoImage + index}
                                    source={{ uri: makeAssetUrl(brandLogoImage) }}
                                    style={{ width: 67, height: 40 }}
                                />
                            ))}
                        </HorizBox>
                    </View>
                    <Pad size={32} />
                </>
            )}
            <View style={s.imageWrapper}>
                <Image source={{ uri: makeAssetUrl(loginPreviewImage) }} style={{ width: 335, height: 215 }} />
            </View>
            <Pad size={32} />
            <Heading
                center
                color={colors.colorTextPrimary}
                formatParams={{ psiBrandName }}
                label={headingLabel}
                type='large'
                weight='medium'
            />
            {subheadingLabel && (
                <>
                    <Pad size={4} />
                    <Paragraph
                        center
                        color={colors.colorTextPrimary}
                        formatParams={{ psiBrandName }}
                        label={subheadingLabel}
                        type='small'
                    />
                </>
            )}
        </PadBox>
    );
}

export type UnauthenticatedLoginScreenProps = { action: string };

export function UnauthenticatedLoginScreen({ action }: UnauthenticatedLoginScreenProps) {
    const s = useUnauthenticatedLoginScreenStyles();
    const { colors } = useTheme();
    const siloKey = useSiloKey();
    const showGoogle = usesFirebaseAuth && silosWithoutGoogleLogin.indexOf(siloKey) === -1;
    const isAdminLogin = action === 'admin';
    const { loginHeadingLabel, loginHeadingLabelAdmin, loginSubheadingLabelAdmin, loginSubheadingLabel, showCookieNotice } =
        useLoginConfig();
    const headingLabel = isAdminLogin ? loginHeadingLabelAdmin : loginHeadingLabel;
    const subheadingLabel = isAdminLogin ? loginSubheadingLabelAdmin : loginSubheadingLabel;
    const deploymentConfig = getDeploymentConfig();
    const psiBrandName = deploymentConfig.psiBrandName ?? 'PSI';

    return (
        <ConversationScreen backgroundColor={colors.colorSurfaceHighlight} noWrap>
            <WindowTitle formatParams={{ psiBrandName }} label={headingLabel} />
            <View style={s.outer}>
                <Pad size={80} />
                <LoginHeader headingLabel={headingLabel} subheadingLabel={subheadingLabel} />
                <Pad size={32} />
                <View style={s.loginButtonsWrapper}>
                    {deploymentConfig.enableDevLogin && <DevLogin />}
                    {showGoogle && <GoogleLogin />}
                    {loginProviders.map((loginProvider) => (
                        <SSOLogin key={loginProvider.key} loginProvider={loginProvider} />
                    ))}
                </View>
                <Pad size={32} />
                {!isAdminLogin && showCookieNotice && (
                    <PadBox bottom={20} horiz={20}>
                        <CookieNotice />
                    </PadBox>
                )}
                <Pad size={40} />
            </View>
        </ConversationScreen>
    );
}

const useUnauthenticatedLoginScreenStyles = createThemedStyles((tokens) => ({
    outer: {
        backgroundColor: tokens.colors.colorSurfaceHighlight,
        flex: 1,
        alignItems: 'center',
    },
    imageWrapper: {
        position: 'relative',
        alignItems: 'center',
    },
    loginButtonsWrapper: {
        alignItems: 'center',
    },
    bubble: {
        position: 'absolute',
        maxWidth: 210,
        minHeight: 30,
        borderRadius: 32,
        backgroundColor: tokens.colors.colorEntryPointColorGreen,
        top: -20,
        zIndex: 1,
        borderColor: tokens.colors.colorSurfaceHighlight,
        borderWidth: 1.5,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    footer: {
        maxWidth: 400,
        minWidth: 375,
    },
}));

let loginProviders: LoginProvider[] = [];
export function registerLoginProviders(providers: LoginProvider[]) {
    loginProviders = [...loginProviders, ...providers];
}

function GoogleLogin() {
    const session = useSession();
    const datastore = useDatastore();

    return (
        <PadBox>
            <CTAButton
                icon={<LoginProviderIcon providerIcon='google.png' />}
                label={t('login.continue_with_google', 'Continue with Google')}
                onPress={() => loginWithGoogleAsync({ datastore, session })}
            />
        </PadBox>
    );
}

function DevLogin() {
    const session = useSession();
    const [showPersonas, setShowPersonas] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    async function onSelectPersona(personaKey: string) {
        setIsLoggingIn(true);
        setShowPersonas(false);
        const result = await authClient.signIn.testing({ id: personaKey });
        if (result.error) {
            setIsLoggingIn(false);
        } else {
            await session.refetch();
        }
    }

    if (isLoggingIn) {
        return (
            <PadBox>
                <CTAButton disabled label={t('login.logging_in', 'Logging in...')} onPress={() => {}} />
            </PadBox>
        );
    }

    return (
        <>
            <PadBox bottom={20}>
                <CTAButton label={t('login.dev_login', 'Dev Login')} onPress={() => setShowPersonas(true)} />
            </PadBox>
            {showPersonas && (
                <DevLoginModal onClose={() => setShowPersonas(false)} onSelectPersona={onSelectPersona} personas={devLoginPersonas} />
            )}
        </>
    );
}

function makeStateChunk({
    siloKey,
    provider,
    debug,
    noPopup,
}: {
    siloKey: string;
    provider: string;
    debug: boolean;
    noPopup?: boolean;
}): string {
    return siloKey + '-' + provider + '-' + boolToString(debug) + '-' + boolToString(noPopup ?? false);
}

function parseStateChunk(chunk: string): { siloKey: string; provider: string; debug: boolean; noPopup: boolean } {
    const parts = chunk.split('-');
    return {
        siloKey: parts[0],
        provider: parts[1],
        debug: parts[2] === 'true',
        noPopup: parts[3] === 'true',
    };
}

type SSOLoginProps = { loginProvider: LoginProvider };

// TODO: Actually check the nonce on the callback
function SSOLogin({ loginProvider }: SSOLoginProps) {
    const datastore = useDatastore();
    const siloKey = useSiloKey();
    if (!loginProvider.silos.includes(siloKey)) {
        return null;
    }
    return (
        <PadBox top={20}>
            <CTAButton
                formatParams={{ providerName: loginProvider.name }}
                icon={loginProvider.icon ? <LoginProviderIcon providerIcon={loginProvider.icon} /> : null}
                label={t('login.continue_with', 'Continue with {providerName}')}
                onPress={() => loginWithProviderAsync({ datastore, siloKey, loginProvider })}
                type='secondary'
            />
        </PadBox>
    );
}

type TokenRedirectScreenProps = { token: string; provider: string };

export function TokenRedirectScreen({ token, provider }: TokenRedirectScreenProps) {
    const datastore = useDatastore();

    const handleToken = useCallback(async () => {
        await authClient.signIn.social(
            {
                provider,
                idToken: {
                    token,
                },
                requestSignUp: true,
            },
            { throw: true },
        );
        datastore.closeWindow();
    }, [token, provider, datastore]);

    useEffect(() => {
        fireAndForget(handleToken());
    }, [handleToken]);

    return <LoggingInIndicator />;
}

function getFragmentParams(fragment: string): Record<string, string> {
    const fragmentContent = fragment.startsWith('#') ? fragment.substring(1) : fragment;
    const params = new URLSearchParams(fragmentContent);
    return Object.fromEntries(params.entries());
}

export function FragmentRedirectScreen() {
    const session = useSession();
    const datastore = useDatastore();
    const fragment = datastore.getUrlFragment();

    const loginWithTokenFragment = useCallback(async () => {
        const fragmentParams = getFragmentParams(fragment);

        if (!fragmentParams.state) {
            console.warn('SSO Login: Missing state parameter in URL fragment.', { fragmentParams });
            trackEvent(null, 'warning', {
                message: 'SSO Login failed due to missing state parameter',
                details: fragmentParams,
                labels: { issueType: 'SSO configuration' },
            });
            return;
        }

        const ssoToken = fragmentParams.id_token;
        const state = parseStateChunk(fragmentParams.state);
        const provider = state.provider;

        if (state.noPopup) {
            const result = await authClient.signIn.social({
                provider,
                idToken: {
                    token: ssoToken,
                },
                requestSignUp: true,
            });
            if (!result.error) {
                await session.refetch();
            }
        } else {
            window?.opener?.postMessage({ type: 'psi-login', ssoToken, provider }, '*');
            if (state.siloKey !== 'test') {
                datastore.closeWindow();
            }
        }
    }, [fragment, session, datastore]);

    useEffect(() => {
        fireAndForget(loginWithTokenFragment());
    }, [loginWithTokenFragment]);

    return <LoggingInIndicator />;
}

export function CodeRedirectScreen() {
    const s = useRedirectScreenStyles();
    const session = useSession();
    const datastore = useDatastore();
    const [error, setError] = useState<string | null>(null);

    const handleCodeRedirect = useCallback(async () => {
        try {
            // Read code and state from the URL query parameters directly, since
            // Expo Router screen params may not pass them through.
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const state = params.get('state');

            if (!code || !state) {
                setError('Missing code or state parameter.');
                return;
            }

            const parsedState = parseStateChunk(state);
            const provider = parsedState.provider;

            // Determine the redirect_uri that was used in the authorization
            // request. It must match exactly for the token exchange to succeed.
            const siloKey = parsedState.siloKey;
            const redirectUri = getBaseUrl() + siloKey + '/login/one/codeRedirect';

            // Exchange the authorization code for an ID token via the server.
            const exchangeResponse = await fetch(`${getApiBaseUrl()}sso/exchange-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ providerId: provider, code, redirectUri }),
            });

            if (!exchangeResponse.ok) {
                const errorBody = await exchangeResponse.text();
                console.error('SSO Code Login: Code exchange failed:', errorBody);
                setError('Login failed. Please try again.');
                return;
            }

            const { idToken } = (await exchangeResponse.json()) as { idToken: string };

            // Sign in using the ID token, same as the fragment flow.
            await authClient.signIn.social(
                {
                    provider,
                    idToken: { token: idToken },
                    requestSignUp: true,
                },
                { throw: true },
            );

            if (parsedState.noPopup) {
                await session.refetch();
            } else {
                window?.opener?.postMessage({ type: 'psi-login', ssoToken: idToken, provider }, '*');
                datastore.closeWindow();
            }
        } catch (e) {
            console.error('SSO Code Login: Unexpected error:', e);
            setError('Login failed. Please try again.');
        }
    }, [datastore, session]);

    useEffect(() => {
        fireAndForget(handleCodeRedirect());
    }, [handleCodeRedirect]);

    if (error) {
        return (
            <View style={s.container}>
                <PadBox horiz={20} vert={20}>
                    <UtilityText
                        label={t('login.sso_error', 'Something went wrong during login. Please close this window and try again.')}
                    />
                </PadBox>
            </View>
        );
    }

    return <LoggingInIndicator />;
}

function LoginProviderIcon({ providerIcon }: { providerIcon: string }) {
    return <Image source={{ uri: makeAssetUrl(`images/${providerIcon}`) }} style={{ width: 20, height: 20 }} />;
}

function CookieNotice() {
    const s = useCookieNoticeStyles();
    const { colors } = useTheme();

    const siloKey = useSiloKey();
    const { privacyNoticeLink } = useLegalDocLinks(siloKey);
    const siloConfig = deploymentConfig?.siloConfig?.[siloKey];

    if (siloConfig?.hideCookieNoticeBanner) {
        return null;
    }

    return (
        <View style={s.container}>
            <RichText
                color={colors.colorTextPrimary}
                formatParams={{ privacyNoticeLink }}
                label={t(
                    'login.cookie_notice',
                    '🍪 This discussion space uses only essential cookies. We do not use cookies for advertising purposes. [Learn more]({privacyNoticeLink}).',
                )}
            />
        </View>
    );
}
const useCookieNoticeStyles = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfaceHighlightShade2,
        padding: 20,
        borderRadius: 8,
    },
}));

function LoggingInIndicator() {
    const s = useRedirectScreenStyles();
    const { colors } = useTheme();
    return (
        <View style={s.container}>
            <PadBox horiz={20} vert={20}>
                <ActivityIndicator color={colors.colorPrimaryBrandIris} size='large' />
                <Pad size={16} />
                <UtilityText label={t('login.logging_in', 'Logging in...')} weight='strong' />
            </PadBox>
        </View>
    );
}

const useRedirectScreenStyles = createThemedStyles((tokens) => ({
    container: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
}));

function useLoginListener() {
    const session = useSession();

    const callback = useCallback(
        async (event: MessageEvent) => {
            if (event.data?.type === 'psi-login') {
                const { ssoToken, provider } = event.data;
                const result = await authClient.signIn.social({
                    provider,
                    idToken: {
                        token: ssoToken,
                    },
                    requestSignUp: true,
                });
                if (!result.error) {
                    await session.refetch();
                }
            }
        },
        [session],
    );

    useEffect(() => {
        window.addEventListener('message', callback);
        return () => {
            window.removeEventListener('message', callback);
        };
    });
}
